import React, { useState } from 'react';
import {
    Box, Typography, Container, Grid, Paper, Button, useTheme, Card, CardContent, CardActions, Chip, Divider, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
    // FIX 2: Added List and ListItem
    List, ListItem,
} from '@mui/material';
import {
    MonetizationOn as MonetizationOnIcon, Upgrade as UpgradeIcon, ArrowDownward as DownloadIcon, Description as InvoiceIcon, PeopleAlt as UserIcon, CalendarToday as CalendarIcon, CheckCircle as CheckIcon, Cancel as CancelIcon, Settings as SettingsIcon,
    // FIX 1: Added StorageIcon
    Storage as StorageIcon
} from '@mui/icons-material';

// --- MOCK Data ---
const currentPlan = {
    name: 'Enterprise Pro',
    price: '$5000/month',
    status: 'Active',
    employees: 4000,
    maxEmployees: 5000,
    cycleEnd: '2025-10-31',
    storageUsed: 78, // in %
};

const pricingTiers = [
    { name: 'Standard', price: '$1000/month', employees: 500, features: ['Core HR Modules', 'Basic Reporting'], isCurrent: false },
    { name: 'Professional', price: '$2500/month', employees: 2000, features: ['Advanced Analytics', 'SSO Integration'], isCurrent: false },
    { name: 'Enterprise Pro', price: '$5000/month', employees: 5000, features: ['Dedicated Support', 'Custom Integrations', 'Unlimited Storage'], isCurrent: true },
];

const mockInvoices = [
    { id: 'INV-00124', date: '2025-09-01', amount: 5000, status: 'Paid', downloadUrl: '#' },
    { id: 'INV-00123', date: '2025-08-01', amount: 5000, status: 'Paid', downloadUrl: '#' },
    { id: 'INV-00122', date: '2025-07-01', amount: 5000, status: 'Paid', downloadUrl: '#' },
    { id: 'INV-00121', date: '2025-06-01', amount: 4800, status: 'Paid', downloadUrl: '#' },
    { id: 'INV-00120', date: '2025-05-01', amount: 4800, status: 'Paid', downloadUrl: '#' },
    { id: 'INV-00119', date: '2025-04-01', amount: 5200, status: 'Pending', downloadUrl: '#' },
];

// --- Sub-Component: Invoice History Table ---
const InvoiceHistoryTable = ({ invoices, theme }) => (
    <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="medium">
            <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Invoice ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Billing Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {invoices.map((invoice) => (
                    <TableRow 
                        key={invoice.id} 
                        sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            '&:hover': { bgcolor: theme.palette.action.hover, transition: 'background-color 0.2s' }
                        }}
                    >
                        <TableCell component="th" scope="row">{invoice.id}</TableCell>
                        <TableCell>{invoice.date}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>${invoice.amount.toLocaleString()}</TableCell>
                        <TableCell align="center">
                            <Chip 
                                label={invoice.status}
                                size="small"
                                color={invoice.status === 'Paid' ? 'success' : 'warning'}
                                icon={invoice.status === 'Paid' ? <CheckIcon sx={{ fontSize: 16 }} /> : <CalendarIcon sx={{ fontSize: 16 }} />}
                                sx={{ fontWeight: 600 }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Tooltip title={`Download ${invoice.id}`}>
                                <IconButton size="small" href={invoice.downloadUrl}>
                                    <DownloadIcon color="primary" fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);


// --- Main Billing & Licensing Component ---
const BillingLicensing = () => {
    const theme = useTheme();

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box 
                sx={{ 
                    mb: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    animation: 'headerFadeIn 0.8s ease-out forwards',
                    '@keyframes headerFadeIn': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={700} 
                    color="primary.dark"
                    sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
                >
                    <MonetizationOnIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'inherit' }} />
                    Billing & Licensing Control
                </Typography>
                <Button variant="outlined" startIcon={<SettingsIcon />} color="primary" sx={{ px: 3 }}>
                    Payment Settings
                </Button>
            </Box>

            {/* Current Plan Card and Usage Metrics */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} md={7}>
                    <Paper 
                        elevation={6} 
                        sx={{ 
                            p: 4, 
                            borderRadius: 3,
                            // Animation: Slide-in from left
                            opacity: 0,
                            transform: 'translateX(-20px)',
                            animation: 'slideInLeft 1s ease-out 0.2s forwards',
                            '@keyframes slideInLeft': { 'to': { opacity: 1, transform: 'translateX(0)' } }
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h5" fontWeight={700} color="error.dark">
                                Your Current Plan:
                            </Typography>
                            <Chip label={currentPlan.status} color="success" size="medium" sx={{ fontWeight: 700 }} />
                        </Box>
                        <Typography variant="h3" fontWeight={800} color="primary.main">
                            {currentPlan.name}
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                            {currentPlan.price}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* Usage Metrics */}
                        <Box sx={{ mb: 3 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body1" fontWeight={600} startIcon={<UserIcon />}>
                                    Employee Limit Usage
                                </Typography>
                                <Typography variant="body1" fontWeight={700}>
                                    {currentPlan.employees.toLocaleString()} / {currentPlan.maxEmployees.toLocaleString()}
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(currentPlan.employees / currentPlan.maxEmployees) * 100} 
                                color="primary" 
                                sx={{ height: 10, borderRadius: 5, transition: 'width 0.5s' }} 
                            />
                            <Typography variant="caption" color="text.secondary">
                                Usage is at {((currentPlan.employees / currentPlan.maxEmployees) * 100).toFixed(1)}% of your plan limit.
                            </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 3 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                {/* FIX: StorageIcon is now imported */}
                                <Typography variant="body1" fontWeight={600} startIcon={<StorageIcon />}>
                                    Cloud Storage Usage
                                </Typography>
                                <Typography variant="body1" fontWeight={700} color={currentPlan.storageUsed > 75 ? 'error.main' : 'warning.main'}>
                                    {currentPlan.storageUsed}% Used
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={currentPlan.storageUsed} 
                                color={currentPlan.storageUsed > 75 ? 'error' : 'warning'}
                                sx={{ height: 10, borderRadius: 5, transition: 'width 0.5s' }} 
                            />
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                            <Typography variant="body2" color="text.secondary" startIcon={<CalendarIcon />}>
                                Next Renewal Date: <Typography component="span" fontWeight={700} color="text.primary">{currentPlan.cycleEnd}</Typography>
                            </Typography>
                            <Button variant="text" color="error">
                                Cancel Subscription
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Pricing Tiers/Upgrade Options (Fixed Width for better hierarchy) */}
                <Grid item xs={12} md={5}>
                    <Box
                        sx={{
                            // Animation: Slide-in from right
                            opacity: 0,
                            transform: 'translateX(20px)',
                            animation: 'slideInRight 1s ease-out 0.4s forwards',
                            '@keyframes slideInRight': { 'to': { opacity: 1, transform: 'translateX(0)' } }
                        }}
                    >
                        <Typography variant="h5" fontWeight={700} color="secondary.dark" sx={{ mb: 2 }}>
                            Explore Plans
                        </Typography>
                        {pricingTiers.filter(t => !t.isCurrent).map((tier, index) => (
                            <Card 
                                key={tier.name} 
                                elevation={index === 0 ? 3 : 1} 
                                sx={{ 
                                    mb: 2, 
                                    border: `1px solid ${theme.palette.divider}`,
                                    transition: 'box-shadow 0.3s, transform 0.3s',
                                    '&:hover': { 
                                        boxShadow: theme.shadows[6],
                                        transform: 'translateY(-3px)' 
                                    }
                                }}
                            >
                                <CardContent>
                                    <Typography variant="h6" fontWeight={700}>{tier.name}</Typography>
                                    <Typography variant="h5" fontWeight={800} color="primary.main">{tier.price}</Typography>
                                    <Chip 
                                        label={`Max ${tier.employees.toLocaleString()} Employees`} 
                                        size="small" 
                                        icon={<UserIcon sx={{ fontSize: 16 }} />}
                                        sx={{ mt: 1, mb: 1, bgcolor: theme.palette.info.light, color: theme.palette.info.dark, fontWeight: 600 }}
                                    />
                                    {/* FIX: List and ListItem are now imported */}
                                    <List dense sx={{ listStyleType: 'disc', pl: 2 }}>
                                        {tier.features.map((feature, i) => (
                                            <ListItem disablePadding key={i} sx={{ display: 'list-item' }}>
                                                <Typography variant="body2">{feature}</Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                                <CardActions>
                                    <Button size="large" fullWidth variant="contained" color="secondary" startIcon={<UpgradeIcon />} sx={{ py: 1.5, fontWeight: 700 }}>
                                        Upgrade Now
                                    </Button>
                                </CardActions>
                            </Card>
                        ))}
                    </Box>
                </Grid>
            </Grid>
            
            {/* Invoice History Section */}
            <Box 
                sx={{ 
                    mt: 6, 
                    // Animation: Slide-up effect
                    opacity: 0,
                    transform: 'translateY(20px)',
                    animation: 'slideUp 1s ease-out 0.6s forwards',
                    '@keyframes slideUp': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Typography 
                    variant="h5" 
                    fontWeight={700} 
                    color="primary.dark" 
                    sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
                >
                    <InvoiceIcon sx={{ mr: 1 }} /> Billing History (Last 6 Months)
                </Typography>
                <InvoiceHistoryTable invoices={mockInvoices} theme={theme} />
            </Box>

        </Container>
    );
};

export default BillingLicensing;